import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import useAuthStore from "../../services/AuthStore";
import { LangContext } from "../../LanguageProvider";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  ImageList,
  ImageListItem,
  // Импортируем новые компоненты для модального окна
  Dialog,
  DialogContent,
  IconButton,
  Zoom, // Можно использовать для анимации
} from "@mui/material";
// Импортируем иконку для кнопки закрытия
import CloseIcon from "@mui/icons-material/Close";

export default function NewsDetailPage() {
  const { translations, lang } = useContext(LangContext);
  const { getNews } = useAuthStore();
  const { newsId } = useParams();

  const [newsItem, setNewsItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. СОСТОЯНИЯ ДЛЯ МОДАЛЬНОГО ОКНА ГАЛЕРЕИ
  // Открыто ли модальное окно
  const [openModal, setOpenModal] = useState(false);
  // Индекс текущего выбранного изображения для отображения
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Обработчики модального окна
  const handleOpenModal = index => {
    setSelectedIndex(index);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  // ------------------------------------------

  useEffect(() => {
    const fetchNewsItem = async () => {
      if (!newsId) return;

      setIsLoading(true);
      try {
        const data = await getNews(newsId);
        setNewsItem(data);
      } catch (err) {
        console.error("Ошибка загрузки новости:", err);
        setError("Новость не найдена");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewsItem();
  }, [newsId, getNews]);

  // Рендер загрузки
  if (isLoading) {
    return (
      <Container sx={{ mt: 15, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  // Рендер ошибки
  if (error || !newsItem) {
    return (
      <Container sx={{ mt: 15 }}>
        <Typography variant="h4" color="error" align="center">
          {error || "Новость не найдена."}
        </Typography>
      </Container>
    );
  }

  const title = lang === "kz" ? newsItem.title_kz : newsItem.title_ru;
  const description =
    lang === "kz" ? newsItem.description_kz : newsItem.description_ru;

  // URL текущего выбранного изображения
  const currentImageBase64 = newsItem.images[selectedIndex];
  const currentImageUrl = currentImageBase64
    ? `data:;base64,${currentImageBase64}`
    : null;

  // Рендер страницы
  return (
    <Container maxWidth="lg" sx={{ mt: 15, mb: 5 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        {/* ... (Ваш существующий заголовок, дата и описание) ... */}

        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold", color: "text.primary" }}>
          {title}
        </Typography>

        <Typography variant="caption" color="text.secondary" gutterBottom>
          {translations["publish"]}:{" "}
          {new Date(newsItem.public_date).toLocaleString(
            lang === "kz" ? "kk-KZ" : "ru-RU",
          )}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            whiteSpace: "pre-wrap",
            lineHeight: 1.7,
          }}>
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* ГАЛЕРЕЯ ИЗОБРАЖЕНИЙ */}
        {newsItem.images && newsItem.images.length > 0 && (
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "text.primary" }}>
              Галерея
            </Typography>
            <ImageList variant="masonry" cols={3} gap={8}>
              {newsItem.images.map((base64String, index) => (
                <ImageListItem
                  key={index}
                  // 2. Добавляем обработчик клика
                  onClick={() => handleOpenModal(index)}
                  sx={{ cursor: "pointer" }}>
                  <img
                    src={`data:;base64,${base64String}`}
                    alt={`Изображение к новости ${index + 1}`}
                    loading="lazy"
                    style={{ borderRadius: "8px" }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </Paper>

      {/* 3. КОМПОНЕНТ МОДАЛЬНОГО ОКНА ДЛЯ ПОЛНОЭКРАННОГО ПРОСМОТРА */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="xl" // Максимальная ширина, чтобы изображение было большим
        fullWidth
        TransitionComponent={Zoom} // Добавляем анимацию
        // Для удобства навигации по галерее (опционально)
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)", // Затемненный фон
            boxShadow: "none",
          },
        }}>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "90vh", // Высота модального окна
          }}>
          {/* Кнопка закрытия (X) */}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}>
            <CloseIcon />
          </IconButton>

          {/* Изображение с прокруткой и масштабированием */}
          {currentImageUrl && (
            <Box
              component="img"
              src={currentImageUrl}
              alt={`Изображение ${selectedIndex + 1}`}
              sx={{
                maxWidth: "100%", // Не превышать ширину контейнера
                maxHeight: "100%", // Не превышать высоту контейнера
                objectFit: "contain",
                cursor: "zoom-in", // Стиль для масштабирования (показывает, что можно кликнуть)
                // !!! ВАЖНО !!!: Для реального масштабирования (увеличения/уменьшения)
                // вам потребуется отдельная библиотека (например, `react-image-zoom`
                // или другой компонент для "pinch-to-zoom" / drag)
                // Базовый HTML/CSS не поддерживает масштабирование из коробки.
                // В данном случае, это просто полноэкранный просмотр.
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
